import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import VectorLogo from 'components/VectorLogo';
import { API } from 'api';
import useLocationSearchParams from 'hooks/useLocationSearchParams';
import { enqueueSnackbar } from 'notistack';

type FormValues = {
  email: string;
};

const ResetPasswordForm = (): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation();
  const queryParams = useLocationSearchParams();

  const validationSchema = Yup.object({
    email: Yup.string()
      .required(t('resetPassword.inputs.email.required'))
      .email(t('resetPassword.inputs.email.error')),
  });

  const { control, handleSubmit, setError } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: decodeURIComponent(queryParams.get('email') ?? ''),
    },
    mode: 'onBlur',
  });

  const { isPending, mutate } = useMutation({
    mutationFn: (newLogin: { email: string }) =>
      API.post('auth/password/reset/', newLogin),
  });

  const handleResetPassword: SubmitHandler<FormValues> = (values) => {
    mutate(values, {
      onSuccess: () => {
        history.push(`/reset_password_confirmation`);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        if (error.response?.status !== 400) {
          enqueueSnackbar(t('common.unknownErrorTryAgain'), {
            variant: 'error',
          });
          return;
        }

        const emailError = error.response.data.email;
        if (emailError) {
          const errorMessage = emailError.join('\n');
          setError('email', { type: 'custom', message: errorMessage });
          return;
        }
        // Generic error message
        enqueueSnackbar(t('common.unknownError'), { variant: 'error' });
      },
    });
  };

  return (
    <form
      autoComplete="off"
      onSubmit={handleSubmit(handleResetPassword)}
      noValidate
    >
      <Grid container direction="column" spacing={1}>
        <Grid item>
          <Box sx={{ textAlign: 'center' }}>
            <VectorLogo sx={{ pt: 1 }} />
          </Box>

          <Typography align="center" component="h2">
            <b>{t('resetPassword.subtitle')}</b>
          </Typography>
        </Grid>

        <Grid item container direction="column" spacing={3}>
          <Grid item>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }): JSX.Element => {
                const { ref, ...muiFieldProps } = field;
                return (
                  <TextField
                    id="field-control-email"
                    label={t('resetPassword.inputs.email.label')}
                    fullWidth
                    type="email"
                    {...muiFieldProps}
                    inputRef={ref}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    required
                  />
                );
              }}
            />
          </Grid>

          <Grid item>
            <Button disabled={isPending} type="submit" fullWidth size="large">
              {isPending ? (
                <CircularProgress size={24} color="secondary" />
              ) : (
                t('resetPassword.button')
              )}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default ResetPasswordForm;
